import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { APP_BUILD } from '../buildInfo'
import { useEnv } from '../context'
import { useClubOverview, useSingleFlight, useSubscription, useTeam } from '../data/hooks'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import { SliceApiError, type ClubOverview, type SubscriptionSummary } from '../data/types'
import { setThemeChoice, useThemeChoice, type ThemeChoice } from '../platform/theme'
import { useBackTo } from './AthleteScreen'
import { countOf } from './DirectionScreens'

/** После смены клуба: всё перечитать и открыть «Сегодня» нового клуба. */
function useOpenOtherClub() {
  const client = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useEnv()
  return async (text: string) => {
    await client.resetQueries({ queryKey: ['redesign'] })
    toast(text)
    navigate('/today', { replace: true })
  }
}

// ---------------------------------------------------------------------------
// Название клуба (/club/rename): только владелец
// ---------------------------------------------------------------------------

export function ClubRenameScreen() {
  const overview = useClubOverview()
  const back = useBackTo('/club')
  if (overview.isSuccess && overview.data.club.role === 'owner') return <ClubRenameForm club={overview.data.club} />
  return (
    <Screen title="Название клуба" back={back} hideHub>
      {overview.isPending ? (
        <Skeleton rows={1} />
      ) : overview.isError ? (
        <ErrorState title="Не удалось загрузить клуб" onRetry={() => void overview.refetch()} />
      ) : (
        <EmptyState title="Переименовать клуб может владелец" />
      )}
    </Screen>
  )
}

function ClubRenameForm({ club }: { club: ClubOverview['club'] }) {
  const navigate = useNavigate()
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const flight = useSingleFlight()
  const [name, setName] = useState(club.name)
  const [error, setError] = useState<string>()
  const [failed, setFailed] = useState(false)
  const dirty = name.trim() !== club.name
  const leave = useLeaveGuard(dirty)
  const toClub = () => navigate('/club', { replace: true })
  const back = () => leave.guard(toClub)

  const save = async () => {
    if (!name.trim()) {
      setError('Название не может быть пустым')
      return
    }
    setError(undefined)
    setFailed(false)
    try {
      await flight.run(async () => {
        await api.renameClub(name.trim())
        await Promise.all([
          client.invalidateQueries({ queryKey: ['redesign', 'club-overview'] }),
          client.invalidateQueries({ queryKey: ['redesign', 'context'] }),
        ])
        toast('Клуб переименован')
        toClub()
      })
    } catch {
      setFailed(true)
    }
  }

  return (
    <Screen
      title="Название клуба"
      back={back}
      hideHub
      hideNav
      actionBar={
        <>
          <button type="button" className="pc-btn" style={{ flex: 1 }} onClick={back}>
            Отмена
          </button>
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: 2 }} disabled={flight.busy || !dirty} onClick={() => void save()}>
            {flight.busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {failed && <SaveFailedAlert>Название не сохранено, попробуйте ещё раз.</SaveFailedAlert>}
        <Field label="Название клуба" error={error} hint="Его видит вся команда и тренер, которого приглашают.">
          {(p) => <input {...p} className="pc-input" value={name} maxLength={200} autoComplete="off" onChange={(e) => setName(e.target.value)} />}
        </Field>
      </form>
      {leave.dialog}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Вступить в другой клуб по коду (/club/join)
// ---------------------------------------------------------------------------

/** Как шаг «Присоединиться по коду» мастера: сначала что за клуб, потом вступить. */
export function ClubJoinScreen() {
  const { api } = useEnv()
  const back = useBackTo('/club')
  const open = useOpenOtherClub()
  const flight = useSingleFlight()
  const [code, setCode] = useState('')
  const [club, setClub] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const failText = (e: unknown, step: 'check' | 'join') => {
    if (e instanceof SliceApiError) {
      if (e.kind === 'not_found') return 'Код не найден, истёк или уже использован.'
      if (e.kind === 'conflict' && e.message === 'already_member') return 'Вы уже в этом клубе: его можно выбрать в «Другой клуб».'
      if (e.kind === 'limit') return 'В этом клубе на текущем тарифе нет мест для участников. Об этом стоит сказать владельцу клуба.'
    }
    return step === 'check' ? 'Не удалось проверить код. Попробуйте ещё раз.' : 'Не удалось вступить. Попробуйте ещё раз.'
  }

  const check = async () => {
    if (!code.trim()) return
    setError(null)
    try {
      const res = await flight.run(() => api.previewInvite(code))
      if (res) setClub(res.clubName)
    } catch (e) {
      setError(failText(e, 'check'))
    }
  }

  const join = async () => {
    setError(null)
    try {
      await flight.run(async () => {
        await api.acceptInvite(code)
        await open(`Вы в клубе «${club}»`)
      })
    } catch (e) {
      setError(failText(e, 'join'))
    }
  }

  return (
    <Screen title="Вступить в клуб по коду" back={back} hideHub>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Код приглашения даёт владелец другого клуба. Ваш текущий клуб остаётся: переключаться между клубами можно в
        «Клубе».
      </p>
      <div className="pc-form">
        <Field label="Код приглашения">
          {(p) => (
            <input
              {...p}
              className="pc-input"
              value={code}
              autoComplete="off"
              autoCapitalize="characters"
              onChange={(e) => {
                setCode(e.target.value)
                setClub(null)
                setError(null)
              }}
            />
          )}
        </Field>
        {error && (
          <div className="pc-alert" role="alert">
            {error}
          </div>
        )}
        {club ? (
          <>
            <div className="pc-note">Вы вступаете в клуб «{club}» как тренер с полным доступом.</div>
            <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={flight.busy} onClick={() => void join()}>
              {flight.busy ? 'Вступаю…' : `Вступить в «${club}»`}
            </button>
          </>
        ) : (
          <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={flight.busy || !code.trim()} onClick={() => void check()}>
            {flight.busy ? 'Проверяю…' : 'Проверить код'}
          </button>
        )}
      </div>
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Выйти из клуба (не владелец)
// ---------------------------------------------------------------------------

export function LeaveClubSheet({ overview, onClose }: { overview: ClubOverview; onClose: () => void }) {
  const { api } = useEnv()
  const open = useOpenOtherClub()
  const flight = useSingleFlight()
  const [error, setError] = useState<string | null>(null)
  const next = overview.clubs.find((c) => c.id !== overview.club.id)

  const leave = async () => {
    setError(null)
    try {
      await flight.run(async () => {
        const res = await api.leaveClub()
        await open(`Вы вышли из клуба «${overview.club.name}». Открыт «${res.switchedTo}»`)
      })
    } catch (e) {
      setError(
        e instanceof SliceApiError && e.message === 'last_club'
          ? 'Это ваш единственный клуб: выйти можно, когда появится другой.'
          : 'Не удалось выйти. Попробуйте ещё раз.',
      )
    }
  }

  return (
    <Sheet title={`Выйти из клуба «${overview.club.name}»?`} onClose={onClose}>
      {!next ? (
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          Это ваш единственный клуб: после выхода работать было бы негде. Сначала вступите в другой клуб по коду - потом
          из этого можно выйти.
        </p>
      ) : (
        <>
          <div className="pc-aa-note pc-aa-note--danger">
            <b>Вы потеряете доступ</b>
            <span>- к направлениям, группам, спортсменам и расписанию клуба;</span>
            <span>- вернуться можно только по новому приглашению владельца.</span>
          </div>
          <div className="pc-aa-note">
            <b>Не изменится</b>
            <span>- данные клуба: всё, что вы вносили, остаётся у клуба;</span>
            <span>- ваш аккаунт и другие клубы. После выхода откроется «{next.name}».</span>
          </div>
          {error && <SaveFailedAlert>{error}</SaveFailedAlert>}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={flight.busy}
            onClick={() => void leave()}
          >
            {flight.busy ? 'Выхожу…' : 'Выйти из клуба'}
          </button>
        </>
      )}
      <button type="button" className="pc-btn pc-btn--block" onClick={onClose}>
        Отмена
      </button>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Удалить клуб (/club/delete): владелец, серьёзное подтверждение
// ---------------------------------------------------------------------------

/**
 * По тому, как устроен сервер: клуб удаляется мягко и только если в нём нет
 * других участников и у владельца есть другой клуб. Всё внутри клуба
 * пропадает из приложения вместе с ним, автопродление больше не списывает
 * (удалённые клубы не продлеваются), оплаченный срок не возвращается.
 */
export function ClubDeleteScreen() {
  const back = useBackTo('/club')
  const overview = useClubOverview()
  const team = useTeam()
  const sub = useSubscription()

  let body
  if (overview.isPending || team.isPending || sub.isPending) body = <Skeleton rows={3} label="Проверяю клуб" />
  else if (overview.isError || team.isError || sub.isError)
    body = (
      <ErrorState
        title="Не удалось проверить клуб"
        text="Без этого не показать, что пропадёт. Удаление недоступно, пока не загрузится."
        onRetry={() => {
          void overview.refetch()
          void team.refetch()
          void sub.refetch()
        }}
      />
    )
  else {
    const o = overview.data
    const others = team.data.members.filter((m) => !m.isMe)
    const next = o.clubs.find((c) => c.id !== o.club.id)
    if (o.club.role !== 'owner') body = <EmptyState title="Удалить клуб может владелец" />
    else if (others.length > 0)
      body = (
        <EmptyState
          title="В клубе есть другие участники"
          text={`Удалить клуб можно, когда в нём не осталось никого, кроме владельца. Сейчас: ${others
            .map((m) => m.name)
            .join(', ')}. Участник выходит сам: «Клуб» -> «Выйти из клуба».`}
        />
      )
    else if (!next)
      body = (
        <EmptyState
          title="Это ваш единственный клуб"
          text="После удаления работать было бы негде. Удалить можно, когда у вас есть другой клуб: вступите в него по коду."
        />
      )
    else {
      const used = (key: string) => sub.data.limits.find((l) => l.key === key)?.used ?? 0
      body = (
        <ClubDeleteConfirm
          club={o.club}
          nextName={next.name}
          counts={{ directions: o.directions.length, groups: used('groups'), coaches: used('coaches') }}
          state={sub.data.state}
          expiresAt={sub.data.expiresAt}
          autoRenewal={sub.data.autoRenewal}
        />
      )
    }
  }

  return (
    <Screen title="Удалить клуб" back={back} hideHub>
      {body}
    </Screen>
  )
}

function ClubDeleteConfirm({
  club,
  nextName,
  counts,
  state,
  expiresAt,
  autoRenewal,
}: {
  club: ClubOverview['club']
  nextName: string
  counts: { directions: number; groups: number; coaches: number }
  state: SubscriptionSummary['state']
  expiresAt?: string
  autoRenewal: boolean
}) {
  const { api } = useEnv()
  const open = useOpenOtherClub()
  const flight = useSingleFlight()
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const matches = typed.trim().toLocaleLowerCase('ru') === club.name.trim().toLocaleLowerCase('ru')
  const until = parseIsoDate(expiresAt?.slice(0, 10))

  const remove = async () => {
    if (!matches) return
    setError(null)
    try {
      await flight.run(async () => {
        const res = await api.deleteClub()
        await open(`Клуб удалён: ${club.name}. Открыт «${res.switchedTo}»`)
      })
    } catch (e) {
      const m = e instanceof SliceApiError ? e.message : ''
      setError(
        m === 'has_members'
          ? 'В клубе появились другие участники: удалить можно, когда они выйдут.'
          : m === 'last_club'
            ? 'Других клубов у вас не осталось: удалить этот нельзя.'
            : 'Клуб не удалён, попробуйте ещё раз.',
      )
    }
  }

  return (
    <>
      <div className="pc-aa-note pc-aa-note--danger" style={{ marginTop: 16 }}>
        <b>Пропадёт из приложения клуб «{club.name}» целиком</b>
        <span>
          - {countOf(counts.directions, 'направление', 'направления', 'направлений')},{' '}
          {countOf(counts.groups, 'группа', 'группы', 'групп')}, {countOf(counts.coaches, 'тренер', 'тренера', 'тренеров')}{' '}
          справочника;
        </span>
        <span>- все спортсмены клуба, и на паузе тоже;</span>
        <span>- расписание, журнал посещений, отметки оплат, соревнования, материалы, разговоры Штаба;</span>
        <span>- приглашения в клуб: по их кодам вступить больше нельзя.</span>
      </div>
      <div className="pc-aa-note">
        <b>Подписка</b>
        <span>
          {state === 'active'
            ? `- оплаченный срок${until ? ` (до ${formatDayShort(until)} ${until.getFullYear()})` : ''} не возвращается;`
            : state === 'trial'
              ? '- пробный период клуба закончится вместе с ним;'
              : '- платной подписки сейчас нет: денег это не касается;'}
        </span>
        <span>{autoRenewal ? '- автопродление больше ничего не спишет.' : '- автопродление выключено.'}</span>
      </div>
      <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
        Данные остаются в базе, но вернуть клуб из приложения нельзя. После удаления откроется «{nextName}».
      </p>
      <Field label={`Чтобы подтвердить, введите название клуба: ${club.name}`}>
        {(p) => <input {...p} className="pc-input" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />}
      </Field>
      {error && <SaveFailedAlert>{error}</SaveFailedAlert>}
      <button
        type="button"
        className="pc-btn pc-btn--block pc-btn--danger"
        style={{ marginTop: 12 }}
        disabled={flight.busy || !matches}
        onClick={() => void remove()}
      >
        {flight.busy ? 'Удаляю…' : 'Удалить клуб'}
      </button>
    </>
  )
}

// ---------------------------------------------------------------------------
// Тема (как в системе / светлая / тёмная)
// ---------------------------------------------------------------------------

const THEMES: [ThemeChoice, string][] = [
  ['system', 'Как в системе'],
  ['light', 'Светлая'],
  ['dark', 'Тёмная'],
]

export function ThemePicker() {
  const { platform } = useEnv()
  const choice = useThemeChoice()
  return (
    <div className="pc-card" style={{ marginTop: 8 }}>
      <div className="pc-kv__label" id="pc-theme-label">
        Оформление
      </div>
      <div className="pc-segments pc-segments--tall" role="group" aria-labelledby="pc-theme-label" style={{ marginTop: 6 }}>
        {THEMES.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={choice === value} onClick={() => setThemeChoice(value)}>
            {label}
          </button>
        ))}
      </div>
      <p className="pc-muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
        {platform.kind === 'web'
          ? 'Только на этом устройстве. «Как в системе» - по настройке телефона или компьютера.'
          : `Только на этом устройстве. «Как в системе» - как в ${platform.kind === 'max' ? 'MAX' : 'Telegram'}.`}
      </p>
    </div>
  )
}

// Обратная связь (/club/feedback) - переписка, см. FeedbackScreens.tsx.

// ---------------------------------------------------------------------------
// О приложении (/club/about)
// ---------------------------------------------------------------------------

const PLATFORM_LABEL = { telegram: 'Мини-приложение в Telegram', max: 'Мини-приложение в MAX', web: 'Приложение в браузере (PWA)' } as const

export function AboutScreen() {
  const { platform } = useEnv()
  const back = useBackTo('/club')
  return (
    <Screen title="О приложении" back={back} hideHub>
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Приложение</div>
            <div className="pc-kv__value">ProCoach - помощник тренера</div>
          </div>
        </li>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Где открыто</div>
            <div className="pc-kv__value">{PLATFORM_LABEL[platform.kind]}</div>
          </div>
        </li>
        <li>
          <div className="pc-kv">
            <div className="pc-kv__label">Сборка</div>
            <div className="pc-kv__value">{APP_BUILD}</div>
          </div>
        </li>
      </ul>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
        Сборку стоит назвать в обращении об ошибке: по ней видно, какая версия у вас открыта.
      </p>
    </Screen>
  )
}
